/**
 * Multi-surface web deploy (RSW-325 / subdomain routing ADR).
 * One promoted web image serves portal, admin, or jobseeker hosts via runtime config.
 *
 * Cloud Run: RSWORKS_APP_SURFACE → runtime-config.js
 * Local dev: VITE_APP_SURFACE (defaults to `all` — combined SPA)
 */

const env = import.meta.env;

/** @typedef {'all' | 'portal' | 'admin' | 'jobseeker'} AppSurface */

export const APP_SURFACES = Object.freeze(['all', 'portal', 'admin', 'jobseeker']);

const DEFAULT_APP_SURFACE = 'all';

function readRuntimeConfig() {
  if (typeof window === 'undefined') return null;
  const config = window.__RSWORKS_RUNTIME_CONFIG__;
  return config && typeof config === 'object' ? config : null;
}

/**
 * Normalize a surface string; unknown values fall back to `all`.
 * @param {unknown} value
 * @returns {AppSurface}
 */
export function normalizeAppSurface(value) {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (APP_SURFACES.includes(normalized)) {
    return /** @type {AppSurface} */ (normalized);
  }
  return DEFAULT_APP_SURFACE;
}

/**
 * Resolved app surface for this web deploy (runtime > Vite env > `all`).
 * @returns {AppSurface}
 */
export function getAppSurface() {
  const runtime = readRuntimeConfig();
  const runtimeSurface = runtime?.appSurface;
  if (runtimeSurface !== undefined && runtimeSurface !== null && runtimeSurface !== '') {
    return normalizeAppSurface(runtimeSurface);
  }
  const viteSurface = env.VITE_APP_SURFACE;
  if (viteSurface !== undefined && viteSurface !== null && viteSurface !== '') {
    return normalizeAppSurface(viteSurface);
  }
  return DEFAULT_APP_SURFACE;
}

/** True when a single persona surface is configured (not combined local dev). */
export function isRestrictedAppSurface() {
  return getAppSurface() !== 'all';
}

export function isPortalSurface() {
  return getAppSurface() === 'portal';
}

export function isAdminSurface() {
  return getAppSurface() === 'admin';
}

export function isJobseekerSurface() {
  return getAppSurface() === 'jobseeker';
}
